import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import Login from "../components/Auth/Login";
import { EventsSettings } from '../components/Events/EventsSettings';
import { GroupIQ } from '../components/GroupIQ/GroupIQ';
import GroupPricing from "../components/GroupPricing";
import NewPassword from "../components/Auth/NewPassword";
import { PrivateRoute, ProtectedRoute } from './PrivateRoute';
import DashboardLayout from "../components/Dashboard/DashboardLayout";
import { clearMessage } from "../actions/message";
import { history } from "./history";
import ProfileSettings from "../components/Settings";
import { setLastEventLog, cleanIDB, deepCopy, checkLoginEnabled } from "./util"
import { DEBUG_MODE } from "./config-default";
import { APP_CACHE } from "./config";
import { FBNotifications } from "../components/layout/dashboard-ui";
import DisplayOnlineStatus from "../components/DisplayOnlineStatus";

//This is for analytics
import { analytics } from '../hooks/use-analytics';
import moment from 'moment';
import SigmaDashboard from '../components/Dashboard/SigmaDashboard';
import LodgiqUserGuideDashboard from '../components/Dashboard/LodgiqUserGuideDashboard';
import { useTabTracker } from '../hooks/useTabTracker';
import useCheckLogin from '../hooks/useCheckLogin';
import { logout } from '../actions/auth';
import { UPDATE_STATE } from '../actions/types';
import { Box } from '@mui/material';
import useInactivityTracker from '../hooks/useInactivityTracker';
import useAutoLogoutOnDateChange from '../hooks/useAutoLogoutOnDateChange';
import VersionUpdateNotifier from './VersionUpdateNotifier';

let APP_RELOADED = true;

// Code is added to start week from sunday in moment js
// moment.updateLocale('en', {
//     week : {
//         dow : 0,
//         doy : 12
//      }
// });

function App() {
    // debugger;
    if (APP_RELOADED) {
        APP_RELOADED = false;
        cleanIDB(); //Clean the IDB on reload
    }

    const { auth, app } = useSelector(state => state);
    const { current_state, prev_state } = app;
    const { app_state, ui_state } = current_state;
    const [activeScreenClass, setActiveScreenClass] = useState('active-screen-');
    const [reload, setReload] = React.useState(0);
    const dispatch = useDispatch();
    const ts = new Date().getTime();
    let [newKey, setNewKey] = useState(ts);

    /** useTabTracker  */
    useAutoLogoutOnDateChange();
    useTabTracker();
    useInactivityTracker();
    const compRef = useRef({ app_state, ui_state });
    // Keep ref in sync every render so deferred callbacks always read settled state
    compRef.current.latest_app_state = app_state;

    //Disable all console log in production
    // if (process.env.NODE_ENV === 'development' && !DEBUG_MODE) {
    if (process.env.NODE_ENV && process.env.NODE_ENV === 'production') {
        console.log = () => { }
        // console.error = () => { }
        console.debug = () => { }
    }

    useEffect(() => {
        //Seting analyze into global widnow variable so that we can access from anywhere

        window.analyze = analytics;
        
        // Initialize analytics with user information for Guides and Surveys
        if (auth.user && auth.profile) {
            const userInfo = {
                user_id: auth.user.id || auth.user.email,
                device_id: auth.user.device_id,
                user_properties: {
                    email: auth.user.email,
                    fullname: auth.user.fullname,
                    property_id: app_state?.property_id,
                    property_name: app_state?.property_name,
                    user_role: auth.profile.user_role,
                    default_view: auth.profile.default_view,
                    ui_view_mode: auth.profile.ui_view_mode
                }
            };
            window.analyze.initialize(userInfo);

            // Seed internal analytics state and set Amplitude user properties at login
            window.analyze.setState({
                currentTile: auth.profile.default_view,
                propertyName: app_state?.property_name,
                propertyId: app_state?.property_id,
            });
            window.analyze.setUserProperties({
                property_name: app_state?.property_name,
                property_id: app_state?.property_id,
                selected_tile: auth.profile.default_view,
                platform: 'web',
            });

            // Variable capacity always starts OFF on a fresh login (not on a page refresh of
            // an already-restored session) — justLoggedIn is only true immediately after
            // login/2FA success, and is reset to false once consumed (see DashboardLayout.js).
            if (auth.profile.justLoggedIn) {
                dispatch({ type: UPDATE_STATE, payload: { ui_state: { showVariableCapacity: false } } });
            }
        } else {
            window.analyze.initialize();
        }
        
        if (ui_state && app_state && !compRef.current.app_state && compRef.current.ui_state) {
            compRef.current.app_state = app_state;
            compRef.current.ui_state = ui_state;
            // APP_CACHE.controls = deepCopy(app_state.workspace_controls);
        }
    }, [auth.user, auth.profile]);

    useEffect(() => {
        const old_app_state = prev_state.app_state;
        const old_ui_state = prev_state.ui_state;

        if (old_ui_state && old_ui_state.active_tiles && ui_state && app_state && ui_state.active_tiles && (ui_state.active_tiles.join() !== old_ui_state.active_tiles.join() || old_app_state.property_id !== app_state.property_id)) {
            const { active_tiles } = ui_state;
            const { property_id } = app_state;

            // Build event metadata from a given appState snapshot + tile list
            const buildTileMeta = (aState, tiles) => {
                const {
                    primary_stay_range: psr, property_id: pid, property_name: pn,
                    user_view: uv, workspace_controls: wc,
                    comp_stay_range: csr, selected_market: sm,
                } = aState;
                const { segmentType: sType, sdlyOn: sdly, dba: dbaV, pickUpType: puType, pickUpDays: puDays, scopeType: scType, dayDate: dd } = wc;
                return {
                    ...psr,
                    property_id: pid, property_name: pn,
                    data_view: uv,
                    data_level: sType,
                    stly_active: !!sdly,
                    day_date: dd,
                    ...(tiles.some(t => ['compset', 'market', 'pace'].includes(t)) && { scope: scType }),
                    ...(tiles.some(t => ['compset', 'market', 'pace', 'bi_sensitivity_analysis'].includes(t)) && { dba_value: dbaV }),
                    ...(tiles.some(t => ['market', 'pickup', 'bi_pickup_analysis'].includes(t)) && { pickup_delta: puDays }),
                    ...(tiles.some(t => ['pickup', 'bi_pickup_analysis'].includes(t)) && { pickup_methodology: puType }),
                    ...(csr && tiles.some(t => ['bi_sensitivity_analysis', 'pace'].includes(t)) && { comp_stay_range: csr }),
                    ...(sm && tiles.some(t => ['market', 'market_summary', 'market_sell_rates'].includes(t)) && { selected_market: sm }),
                    ...(tiles.length === 1 && tiles[0] === 'hotel_summary' && { selected_summary: 'hotel' }),
                    ...(tiles.length === 1 && tiles[0] === 'market_summary' && { selected_summary: 'market' }),
                };
            };

            // Helper: schedule a deferred tile_change event (100ms lets workspace controls settle)
            const scheduleTileEvent = (tileKey, previous_tile) => {
                const lastTrack = compRef.current._lastTileTrack;
                const isDuplicate = lastTrack && lastTrack.tile === tileKey && (Date.now() - lastTrack.ts) < 1000;
                if (isDuplicate) return;
                compRef.current._lastTileTrack = { tile: tileKey, ts: Date.now() };
                const capturedTiles = active_tiles.slice();
                setTimeout(() => {
                    const freshMeta = buildTileMeta(compRef.current.latest_app_state || app_state, capturedTiles);
                    window.analyze.track("tile_change", {
                        previous_tile,
                        selected_tile: tileKey,
                        ...freshMeta,
                    }, { old_app_state, old_ui_state }, current_state);
                }, 100);
            };

            //Record single tile change
            if (active_tiles && old_ui_state && ui_state.active_tiles.length === 1 && old_ui_state.active_tiles[0] !== active_tiles[0]) {
                const previous_tile = old_ui_state.active_tiles.length > 1 ? 'intellimerged tile' : old_ui_state.active_tiles.join();
                scheduleTileEvent(active_tiles.join(), previous_tile);
            }

            //Record intellimerge tile change
            if (active_tiles && old_ui_state && ui_state.active_tiles.length > 1 && ui_state.active_tiles.join() !== old_ui_state.active_tiles.join()) {
                const previous_tile = old_ui_state.active_tiles.length > 1 ? 'intellimerged tile' : old_ui_state.active_tiles.join();
                scheduleTileEvent(active_tiles.join(), previous_tile);
            }

            // Keep selected_tile in sync on every tile change (single or intellimerged)
            if (ui_state.active_tiles.join() !== old_ui_state.active_tiles.join()) {
                const newTile = active_tiles.length > 1 ? 'intellimerged tile' : active_tiles.join();
                window.analyze.setTile(newTile);
                window.analyze.setUserProperties({ selected_tile: newTile });
            }

            //Record Property change
            if (active_tiles && old_app_state && old_app_state.property_id !== property_id) {
                window.analyze.track("property_changed", {
                    "previous_property_id": old_app_state.property_id,
                    "previous_property_name": old_app_state.property_name,
                    ...buildTileMeta(app_state, active_tiles),
                }, { old_app_state, old_ui_state }, current_state);
                // compRef.current.app_state.property_id = property_id;
            }

            //Record Data Source change
            // if (active_tiles && old_app_state && old_app_state.active_data_src && old_app_state.active_data_src.pms !== active_data_src.pms) {
            // window.analyze.track("ds_changed", {
            //     "previous_data_source": old_app_state.active_data_src.pms,
            //     "selected_data_source": active_data_src.pms,
            //     ...event_metadata
            // }, { old_app_state, old_ui_state }, current_state);
            // compRef.current.app_state.active_data_src = app_state.active_data_src;
            // }
            //Record Data Source change

            //Record Room type pricing toggle
            // if (active_tiles && old_app_state && old_app_state.workspace_controls.isRoomTypePricing !== workspace_controls.isRoomTypePricing) {
            // window.analyze.track("rtp_toggle", {
            //     "pr": workspace_controls.isRoomTypePricing ? 'Yes' : 'No',
            //     ...event_metadata
            // }, { old_app_state, old_ui_state }, current_state);
            // compRef.current.app_state.workspace_controls.isRoomTypePricing = workspace_controls.isRoomTypePricing;
            // }


            //Record Alert popup open
            //...That is captured in AlertList.js
        }
        if (old_ui_state && old_ui_state.show_flash_report !== ui_state.show_flash_report
            && ui_state.show_flash_report === true && !auth.profile.justLoggedIn) {
            let openedOnLogin = auth.profile.justLoggedIn
            window.analyze.track("flash_report_opened", {
                "previous_flash_report_popup": 'No',
                "selected_flash_report_popup": 'Yes',
                opened_on_login: openedOnLogin
            }, null, { app_state, ui_state });
        }


        if (ui_state && ui_state.active_tiles) {
            let activeScreenClass = 'active-screen-';
            activeScreenClass += ui_state && ui_state.active_tiles ? ui_state.active_tiles.join().toLowerCase() : '';
            setActiveScreenClass(activeScreenClass);
        }

    }, [
        ui_state,
        app_state,
    ]);

    useEffect(() => {
        let isSubscribed = true;
        history.listen((location) => {
            dispatch(clearMessage()); // clear message when changing location
        });

        return () => (isSubscribed = false);
    }, [dispatch]);

    /** below useEffect is used to logout from all the tab  */
    const hasLoggedOutRef = useRef(false);
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (!event.key || event.key === 'isLoginEnabled') {
                if (!checkLoginEnabled() && !hasLoggedOutRef.current) {
                    hasLoggedOutRef.current = true;
                    dispatch(logout());
                    window.top.location.href = '/login';
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch]);








    const { response, loading, error } = useCheckLogin(checkLoginEnabled());
    if (loading) return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '1.5rem',
                fontWeight: '500',
                color: '#555',
                backgroundColor: '#f9f9f9',
            }}>Authenticating...we're getting things ready...</Box>
    );
    // if (response === false) return <p>Login disabled</p>;
    if (error) return <p>Error: {error.message}</p>;

    return (
        <>
            <div onClick={() => {
                setLastEventLog();
            }} className={"App " + activeScreenClass} style={{ height: '100vh' }}>
                <Router>
                    <Routes>
                        <Route element={<PrivateRoute component={EventsSettings} />} path="/events" />
                        <Route
                            path="/groupIQ"
                            element={<ProtectedRoute component={GroupIQ} requiredFeature="group_v2" />}
                        />
                        <Route
                            path="/Group-IQ"
                            element={<PrivateRoute component={GroupPricing} />}
                        />
                        <Route element={<NewPassword />} path="/new-password" />
                        <Route element={<PrivateRoute component={SigmaDashboard} />} path="/analytics-reports" />
                        <Route element={<PrivateRoute component={SigmaDashboard} />} path="/feeder-market-reports" />
                        <Route element={<PrivateRoute component={LodgiqUserGuideDashboard} />} path="/lodgiq-user-guide" />
                        <Route key={newKey} element={<PrivateRoute reload={reload} component={DashboardLayout} />} path="/" />
                        <Route element={<PrivateRoute component={ProfileSettings} />} path="/settings" />
                        <Route element={<Login />} path="/login" />

                    </Routes>
                </Router>
                <FBNotifications />
                <DisplayOnlineStatus />
            </div>
            <VersionUpdateNotifier />
        </>
    );
}

export default App;
